// CanvasHelpCard — the house floating help / tip card. Extends the generic
// CanvasMessage pill (floating chrome + optional dismiss ×) and layers on the
// glassy canvas-card surface plus a slightly higher bottom offset so the pill
// clears a surface's bottom border instead of kissing it. Consumers supply the
// content (help lines, a Lottie, …) and an optional `dismissKey`.
//
// Used by the /team-and-agents canvas help pill and the home dashboard
// context-menu tip, so both read in-family. (2026-06-29 per Nika.)
export const CanvasHelpCard = {
  extends: 'CanvasMessage',
  // Glassy canvas card surface — the same theme the tickets / infra / chat
  // drawers use — instead of CanvasMessage's flat `card` theme.
  theme: 'canvas-card-glass',
  // Lift off the surface's bottom edge: CanvasMessage's A2 sat flush against the
  // canvas bottom border (and under the floating Zoombar). C1 clears the bottom
  // chrome on the canvas pages with a little breathing room above it.
  // While a call's dock shows in the same corner (the shell's
  // --calldock-clear: its top edge from the viewport bottom), the tip stacks
  // A above it instead of under it. Unset (no shell) or 0 → C1, as before.
  bottom: 'max(var(--spacing-C1), calc(var(--calldock-clear, 0%) + var(--spacing-A)))',
  // The page-sidebar rule (Nika 2026-10-29; shared functions/pageSidebar.js):
  // a tip on a page whose sidebar sits on the RIGHT (beside a left navbar)
  // moves to the bottom-LEFT corner, so it never covers the rail. Opt-in: the
  // instance sets `'data-sidebar-side': (el, s) =>
  // (el.call('pageSidebarRead', s.root, el) || {}).side`.
  '&[data-sidebar-side="right"]': { right: 'auto', left: 'A2' },
  // Every help tip leads with the swipe animation the /team-and-agents HelpBox
  // established (2026-07-03 per Nika "should have lottie animation just like
  // /team-and-agents") — consumers can override `Lottie` (same key merges) or
  // null it out.
  //
  // LMC-F1 (2026-08-11): decorative by construction, not tagged as such —
  // every consumer (CtxMenuTip, tickets board Stage tip, canvas
  // team-and-agents HelpBox) pairs this Lottie with sibling text lines that
  // carry the actual instruction ("Right-click for options", "Drag cards
  // between columns.", …); the swipe mark is a visual accent, never the sole
  // carrier of the message. That already made it EXEMPT under WCAG 1.4.11 —
  // it just wasn't marked, so the AA icon-contrast walker measured it anyway
  // and mis-scored it: the source asset bakes literal white AND black shapes
  // (verified via lottie-web's `renderer:'svg'` output, `paints:2` on a live
  // probe), deliberately composited via CtxMenuTip's own `mixBlendMode:
  // 'difference'` override so either shape reads correctly against the
  // glass card regardless of theme — a technique the walker's plain
  // fill/stroke sampling cannot see (it does not evaluate blend modes), so it
  // flagged the raw baked colors as failing in BOTH themes (1.01 light /
  // 1.08 dark) when the actual composited render is legible. `ariaHidden`
  // makes the existing exemption explicit instead of leaving a decorative
  // animation to keep tripping the scanner — same pattern already used for
  // `LoaderRatio.Spinner`.
  //
  // DESIGN-CANVAS-HELP-CARD-LIGHT-LOTTIE-1 (2026-10-04): the `difference`
  // blend lives HERE, on the shared card, not only on CtxMenuTip — the
  // asset's white shape vanished on the light glass card for every other
  // consumer (/tasks, /team-and-agents, the canvas dashboard). `difference`
  // reads both baked shapes against the card's real surface in either theme,
  // with no per-theme colour.
  Lottie: {
    src: 'swipe-right.json',
    fontSize: 'B1',
    margin: '- auto - auto',
    mixBlendMode: 'difference',
    ariaHidden: 'true'
  }
}
